import {
  DateTimeFormatter,
  Duration,
  LocalDate,
  OffsetDateTime,
} from "@js-joda/core";
import "@js-joda/timezone";
import { InputField } from "components/designV2/inputfield/inputfield";
import { Text } from "components/designV2/text/text";
import { TimePicker } from "components/designV2/timePicker/timePicker";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { ZIndex } from "config/zIndex";
import { CompanyGigShift } from "model/Gig";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  endsSameDay,
  normalizeTimeValue,
  parseZoneId,
  toIsoFromLocal,
  toLocalTimeSafe,
  toSentenceCase,
} from "utils/date-time.logic";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import type { FormData } from "../modal/gigFormModal.types";
import type { OverlappingItem } from "../logic/gigForm.overlap.logic";
import type { WorkersWithoutEmploymentByShiftIndex } from "../logic/gigForm.employment.logic";
import { ListContainer } from "../createGigModal.styled";
import {
  ActionsWrap,
  BreakInputWrap,
  Dash,
  EditableShiftRow,
  EditableShiftRowLeft,
  EditBreakWrap,
  EditDeductionWrap,
  EditShiftMetaRow,
  EditStartTimeWrap,
  EditTimeWrap,
  HiddenDuration,
  ShiftActionsRow,
  ShiftBreakCell,
  ShiftDeductionCell,
  ShiftDateCell,
  ShiftDurationCell,
  ShiftMetaRow,
  ShiftRow,
  ShiftRowLeft,
  ShiftRowRight,
  ShiftTimeCell,
  ShiftWarningCell,
} from "./shiftList.styled";
import {
  getOverlappingShiftIndices,
  prepareSortedShifts,
} from "../logic/shiftList.time.logic";
import {
  parseNonNegativeNumber,
  sanitizeNumericInput,
} from "./shiftForm.logic";
import { ShiftWarning } from "./shiftWarning.component";

type Props = {
  shiftListOpen: boolean;
  shifts: CompanyGigShift[];
  overlapItems?: OverlappingItem[];
  readonly workersWithoutEmploymentByShiftIndex?: WorkersWithoutEmploymentByShiftIndex;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
};

interface EditState {
  originalIndex: number;
  startTime: string;
  endTime: string;
  breakTime: string;
  deductionAmount: string;
}

const MAX_BREAK_DIGITS = 3;
const MAX_DEDUCTION_DIGITS = 6;

export const ShiftList: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const { setFormData } = props;
  const [editState, setEditState] = useState<EditState | null>(null);
  const [companyState] = useCompanyStore();
  const companyTimeZone =
    companyState.companySettings.find(
      (setting) => setting.key === "CompanyTimeZone",
    )?.value ?? "Europe/Stockholm";

  const zoneId = useMemo(
    () => parseZoneId(String(companyTimeZone)),
    [companyTimeZone],
  );
  const unavailableTimeText = t("CreateShift.TimeUnavailable", {
    defaultValue: "--:--",
  });

  const dateFormatter = useMemo(() => DateTimeFormatter.ofPattern("d/M"), []);
  const formatShiftDate = useCallback(
    (isoString: string) => {
      try {
        return OffsetDateTime.parse(isoString)
          .atZoneSameInstant(zoneId)
          .format(dateFormatter);
      } catch {
        return unavailableTimeText;
      }
    },
    [zoneId, dateFormatter, unavailableTimeText],
  );

  const sortedShifts = useMemo(
    () => prepareSortedShifts(props.shifts, zoneId),
    [props.shifts, zoneId],
  );

  const overlappingShiftIndices = useMemo(
    () => getOverlappingShiftIndices(sortedShifts),
    [sortedShifts],
  );
  const overlapWorkerNamesByIndex = useMemo(() => {
    const namesByIndex = new Map<number, string[]>();
    if (!props.overlapItems?.length) return namesByIndex;

    const overlapRangesByName = props.overlapItems
      .map((item) => {
        const workerName =
          `${item.worker.firstName} ${item.worker.lastName}`.trim();
        const ranges = item.shifts
          .map((shift) => {
            try {
              return {
                start: OffsetDateTime.parse(shift.startTime).toInstant(),
                end: OffsetDateTime.parse(shift.endTime).toInstant(),
              };
            } catch {
              return null;
            }
          })
          .filter(
            (
              range,
            ): range is {
              start: ReturnType<OffsetDateTime["toInstant"]>;
              end: ReturnType<OffsetDateTime["toInstant"]>;
            } => range !== null,
          );

        return workerName && ranges.length > 0 ? { workerName, ranges } : null;
      })
      .filter(
        (
          item,
        ): item is {
          workerName: string;
          ranges: Array<{
            start: ReturnType<OffsetDateTime["toInstant"]>;
            end: ReturnType<OffsetDateTime["toInstant"]>;
          }>;
        } => item !== null,
      );

    sortedShifts.forEach(({ originalIndex, startZdt, endZdt }) => {
      const shiftStartInstant = startZdt.toInstant();
      const shiftEndInstant = endZdt.toInstant();
      const matchingNames = overlapRangesByName.reduce<string[]>(
        (names, { workerName, ranges }) => {
          const hasOverlap = ranges.some(
            ({ start, end }) =>
              shiftStartInstant.compareTo(end) < 0 &&
              start.compareTo(shiftEndInstant) < 0,
          );
          if (hasOverlap) {
            names.push(workerName);
          }
          return names;
        },
        [],
      );

      if (matchingNames.length > 0) {
        namesByIndex.set(originalIndex, matchingNames);
      }
    });

    return namesByIndex;
  }, [props.overlapItems, sortedShifts]);

  const saveEditedShift = useCallback(
    (originalIndex: number, rowDate: LocalDate) => {
      if (!editState || editState.originalIndex !== originalIndex) return;
      const normalizedStart = normalizeTimeValue(editState.startTime);
      const normalizedEnd = normalizeTimeValue(editState.endTime);
      if (normalizedStart == null || normalizedEnd == null) return;

      const startIso = toIsoFromLocal(rowDate, normalizedStart, zoneId);
      const endDate = endsSameDay(normalizedStart, normalizedEnd)
        ? rowDate
        : rowDate.plusDays(1);
      const endIso = toIsoFromLocal(endDate, normalizedEnd, zoneId);
      const shiftDurationMinutes = Math.max(
        0,
        Duration.between(
          OffsetDateTime.parse(startIso),
          OffsetDateTime.parse(endIso),
        ).toMinutes(),
      );
      const breakTime = Math.min(
        parseNonNegativeNumber(editState.breakTime),
        shiftDurationMinutes,
      );
      const deductionAmount = parseNonNegativeNumber(editState.deductionAmount);

      setFormData((previousFormData) => ({
        ...previousFormData,
        shifts: previousFormData.shifts.map((existingShift, shiftIndex) =>
          shiftIndex !== originalIndex
            ? existingShift
            : {
                ...existingShift,
                startTime: startIso,
                endTime: endIso,
                breakTime,
                deductionAmount,
              },
        ),
      }));
      setEditState(null);
    },
    [editState, setFormData, zoneId],
  );

  return (
    <>
      {props.shiftListOpen && (
        <ListContainer>
          {sortedShifts.map(({ shift, originalIndex, startZdt, endZdt }) => {
            const breakMinutes = parseNonNegativeNumber(shift.breakTime);
            const deductionAmountValue = parseNonNegativeNumber(
              shift.deductionAmount,
            );
            const totalMinutes = Math.max(
              0,
              Duration.between(startZdt, endZdt).toMinutes() - breakMinutes,
            );
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            const durationText = t("CreateShift.DurationCompact", {
              hours,
              minutes,
              defaultValue: "{{hours}}h {{minutes}}m",
            });
            const breakText = t("GigsWorker.MinutesUnpaidBreak", {
              minutes: breakMinutes,
              defaultValue: "{{minutes}} min break",
            });
            const deductionText = t("CreateShift.DeductionCompact", {
              amount: deductionAmountValue,
              defaultValue: "{{amount}} kr avdrag",
            });
            const startLabel =
              toLocalTimeSafe(shift.startTime, zoneId)
                ?.toString()
                .slice(0, 5) ?? unavailableTimeText;
            const endLabel =
              toLocalTimeSafe(shift.endTime, zoneId)?.toString().slice(0, 5) ??
              unavailableTimeText;
            const weekdayKey = startZdt.dayOfWeek().name();
            const weekdayLabel = toSentenceCase(
              String(t(`WeekDays.${weekdayKey}SHORT`)),
            );
            const isEditing = editState?.originalIndex === originalIndex;
            const rowDate = startZdt.toLocalDate();
            const overlappingWorkerNames =
              overlapWorkerNamesByIndex.get(originalIndex) ?? [];
            const showOverlapWarning =
              overlappingShiftIndices.has(originalIndex) ||
              overlappingWorkerNames.length > 0;
            const workersWithoutEmployment =
              props.workersWithoutEmploymentByShiftIndex?.[originalIndex] ?? [];
            if (isEditing && editState) {
              return (
                <EditableShiftRow
                  key={`${originalIndex}-${shift.startTime}-${shift.endTime}`}
                >
                  <EditableShiftRowLeft>
                    <ShiftDateCell>
                      <Text typography="b1" color={ColorV2.Blue3}>
                        {`${weekdayLabel} ${formatShiftDate(shift.startTime)}`}
                      </Text>
                    </ShiftDateCell>
                    <EditStartTimeWrap>
                      <TimePicker
                        ariaLabel="Shift start"
                        value={editState.startTime}
                        onChange={(value) =>
                          setEditState((prev) =>
                            prev == null ? prev : { ...prev, startTime: value },
                          )
                        }
                        size="compact"
                        menuZIndex={ZIndex.modalOverlay + 1}
                      />
                    </EditStartTimeWrap>
                    <Dash>-</Dash>
                    <EditTimeWrap>
                      <TimePicker
                        ariaLabel="Shift end"
                        value={editState.endTime}
                        onChange={(value) =>
                          setEditState((prev) =>
                            prev == null ? prev : { ...prev, endTime: value },
                          )
                        }
                        size="compact"
                        menuZIndex={ZIndex.modalOverlay + 1}
                      />
                    </EditTimeWrap>
                  </EditableShiftRowLeft>
                  <ShiftRowRight>
                    <EditShiftMetaRow>
                      <EditBreakWrap>
                        <Text typography="b2" color={ColorV2.Blue3}>
                          {t("CreateShift.BreakLabel", {
                            defaultValue: "Rast",
                          })}
                        </Text>
                        <BreakInputWrap>
                          <InputField
                            value={editState.breakTime}
                            onChange={(value) =>
                              setEditState((prev) =>
                                prev == null
                                  ? prev
                                  : {
                                      ...prev,
                                      breakTime: sanitizeNumericInput(
                                        value,
                                        MAX_BREAK_DIGITS,
                                      ),
                                    },
                              )
                            }
                            type="number"
                            size="compact"
                            fullWidth
                          />
                        </BreakInputWrap>
                      </EditBreakWrap>
                      <EditDeductionWrap>
                        <Text typography="b2" color={ColorV2.Blue3}>
                          {t("GigsCompany.Deduction", {
                            defaultValue: "Avdrag",
                          })}
                        </Text>
                        <BreakInputWrap>
                          <InputField
                            value={editState.deductionAmount}
                            onChange={(value) =>
                              setEditState((prev) =>
                                prev == null
                                  ? prev
                                  : {
                                      ...prev,
                                      deductionAmount: sanitizeNumericInput(
                                        value,
                                        MAX_DEDUCTION_DIGITS,
                                      ),
                                    },
                              )
                            }
                            type="number"
                            size="compact"
                            fullWidth
                          />
                        </BreakInputWrap>
                      </EditDeductionWrap>
                      <HiddenDuration>00h 00m</HiddenDuration>
                    </EditShiftMetaRow>
                    <ShiftActionsRow>
                      <ActionsWrap
                        type="button"
                        aria-label="Save shift"
                        onClick={() => saveEditedShift(originalIndex, rowDate)}
                      >
                        <CustomIcon
                          name="v2/checkmark"
                          size="24px"
                          color={ColorV2.Blue1}
                        />
                      </ActionsWrap>
                      <ActionsWrap
                        type="button"
                        aria-label="Cancel edit shift"
                        onClick={() => setEditState(null)}
                      >
                        <CustomIcon
                          name="v2/cross"
                          size="24px"
                          color={ColorV2.Blue1}
                        />
                      </ActionsWrap>
                    </ShiftActionsRow>
                  </ShiftRowRight>
                </EditableShiftRow>
              );
            }

            return (
              <ShiftRow
                key={`${originalIndex}-${shift.startTime}-${shift.endTime}`}
              >
                <ShiftRowLeft>
                  <ShiftWarningCell>
                    <ShiftWarning
                      hasOverlapWarning={showOverlapWarning}
                      overlappingWorkerNames={overlappingWorkerNames}
                      workersWithoutEmployment={workersWithoutEmployment}
                    />
                  </ShiftWarningCell>
                  <ShiftDateCell>
                    <Text typography="b1" color={ColorV2.Blue4}>
                      {`${weekdayLabel} ${formatShiftDate(shift.startTime)}`}
                    </Text>
                  </ShiftDateCell>
                  <ShiftTimeCell>
                    <Text typography="b1" color={ColorV2.Blue1}>
                      {`${startLabel} - ${endLabel}`}
                    </Text>
                  </ShiftTimeCell>
                  <ShiftBreakCell>
                    <Text typography="b2" color={ColorV2.Blue5}>
                      {breakText}
                    </Text>
                  </ShiftBreakCell>
                </ShiftRowLeft>
                <ShiftRowRight>
                  <ShiftMetaRow>
                    <ShiftDeductionCell>
                      <Text typography="b2" color={ColorV2.Blue5}>
                        {deductionText}
                      </Text>
                    </ShiftDeductionCell>
                    <ShiftDurationCell>
                      <Text typography="b2" color={ColorV2.Blue5}>
                        {durationText}
                      </Text>
                    </ShiftDurationCell>
                  </ShiftMetaRow>
                  <ShiftActionsRow>
                    <ActionsWrap
                      type="button"
                      aria-label="Edit shift"
                      onClick={() =>
                        setEditState({
                          originalIndex,
                          startTime: startLabel,
                          endTime: endLabel,
                          breakTime: String(breakMinutes),
                          deductionAmount: String(deductionAmountValue),
                        })
                      }
                    >
                      <CustomIcon
                        name="v2/pen"
                        size="24px"
                        color={ColorV2.Blue1}
                      />
                    </ActionsWrap>
                    <ActionsWrap
                      type="button"
                      aria-label="Delete shift"
                      onClick={() => {
                        props.setFormData((prev) => ({
                          ...prev,
                          shifts: prev.shifts.filter(
                            (_, i) => i !== originalIndex,
                          ),
                        }));
                        if (editState?.originalIndex === originalIndex) {
                          setEditState(null);
                        }
                      }}
                    >
                      <CustomIcon
                        name="v2/trash"
                        size="24px"
                        color={ColorV2.Blue1}
                      />
                    </ActionsWrap>
                  </ShiftActionsRow>
                </ShiftRowRight>
              </ShiftRow>
            );
          })}
        </ListContainer>
      )}
    </>
  );
};
